import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Col, Dropdown, List, Menu, message, Modal, Row, Select, Tag } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

// svgs
import { ReactComponent as IconKeyboardEnter } from '../../../../../../../images/icon-keyboard-return.svg';
import { ReactComponent as IconArrowDown } from '../../../../../../../images/keyboard_arrow_down.svg';
import { ReactComponent as IconMoreVert } from '../../../../../../../images/icon-more-vert.svg';

// components, keys, and utils
import { DefaultValueType } from '../../../../../../shared/tags/DefaultValueType';
import { useGetRecommendations } from '../../../../../../shared/recommendation';
import { getNameFromType, OWNERSHIP_DISPLAY_TYPES } from './ownershipUtils';
import { useEntityRegistry } from '../../../../../../useEntityRegistry';
import CustomAvatar from '../../../../../../shared/avatar/CustomAvatar';
import utilService from '../../../../../../../utils/util.service';
import { handleBatchError } from '../../../../utils';

// generated
import {
    CorpUser,
    Entity,
    EntityType,
    OwnershipType,
    // OwnerInput,
    ResourceRefInput,
} from '../../../../../../../types.generated';
import { useBatchUpdateOwnersMutation } from '../../../../../../../graphql/mutations.generated';
import { useListOwnershipTypesQuery } from '../../../../../../../graphql/ownership.generated';
import { useGetSearchResultsLazyQuery } from '../../../../../../../graphql/search.generated';

const StyledButton = styled(Button)`
    gap: 4px;
`;

const StyledIconButton = styled(Button)`
    padding: 2px !important;
    justify-content: center;
`;

const SelectInput = styled(Select)`
    width: 480px;
`;

const StyledOption = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    .enter-icon {
        opacity: 0;
        width: 10px;
        height: 10px;
        transition: opacity 0.2s ease-in-out;
    }

    .ant-select-item-option-active & .enter-icon {
        opacity: 1;
    }
`;

type SelectedOwnershipType = {
    type: string;
    urn: string;
    info?: {
        description?: string;
        name: string;
    };
};

type OwnerListType = {
    urn: string;
    entity?: Entity | null;
    ownershipTypeUrn?: string;
    ownershipTypeName?: string;
    ownershipType?: string | null;
};

type SelectValueType = {
    key: string;
    label: React.ReactNode;
    value: string;
};

const { debounce } = utilService;
interface Props {
    onCloseModal: () => void;
    isVisible: boolean;
    defaultValues?: DefaultValueType[];
    resources: ResourceRefInput[];
    refetch?: () => void;
}

const UpdateOwnersModal = ({ onCloseModal, isVisible, defaultValues, resources, refetch }: Props) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedOwnerType, setSelectedOwnerType] = useState<SelectedOwnershipType | null>(null);
    const [ownerList, setOwnerList] = useState<OwnerListType[]>([]);
    const [selectValue, setSelectValue] = useState<SelectValueType | null>();
    const [lastSelectedEntity, setLastSelectedEntity] = useState<Entity | null>(null);
    const [batchUpdateOwnersMutation, { loading: loadingMutation }] = useBatchUpdateOwnersMutation();

    // User dropdown search results!
    const [userSearch, { data: userSearchData, loading: loadingSearch }] = useGetSearchResultsLazyQuery();
    const userSearchResults = userSearchData?.search?.searchResults?.map((searchResult) => searchResult.entity) || [];
    const combinedSearchResults = userSearchResults;

    // EntityType.CorpGroup is not been added in useGetRecommendations as currently we only need to show USERS.
    const [recommendedData] = useGetRecommendations([EntityType.CorpUser]);
    const inputEl = useRef(null);

    /**
     * Updates the owners for the specified resources by adding and removing owners in batch.
     *
     * @param ownersToRemove - An array of owners to be removed from the resources.
     * @param ownersToAdd - An array of owners to be added to the resources.
     *
     * @remarks
     * - This function performs a batch mutation to update the owners.
     * - Displays success or error messages based on the operation result.
     * - Closes the modal after the operation is completed.
     * - Refetches the data if the operation is successful.
     */
    const batchUpdateOwners = useCallback(
        (ownersToRemove, ownersToAdd) => {
            batchUpdateOwnersMutation({
                variables: {
                    input: {
                        ownersToRemove,
                        ownersToAdd,
                        resources,
                    },
                },
            })
                .then(({ errors }) => {
                    if (!errors) {
                        message.success({
                            content: `Updated Owners!`,
                            duration: 2,
                        });
                        refetch?.();
                    }
                })
                .catch((e) => {
                    message.destroy();
                    message.error({
                        content: `Updated Owners failed!`,
                        duration: 2,
                    });
                    message.error(
                        handleBatchError([], e, { content: `Failed to Update: \n ${e.message || ''}`, duration: 3 }),
                    );
                })
                .finally(() => {
                    onCloseModal();
                });
        },
        [batchUpdateOwnersMutation, resources, refetch, onCloseModal],
    );

    useEffect(() => {
        if ((defaultValues ?? []).length > 0) {
            const ownersList: OwnerListType[] = (defaultValues ?? []).map((item) => {
                return {
                    urn: item.urn,
                    entity: item.entity,
                    ownershipTypeName:
                        item.ownershipType?.info?.name ?? getNameFromType(item.ownerType as OwnershipType),
                    ownershipTypeUrn: item.ownershipType?.urn ?? '',
                    ownershipType: item.ownerType ?? '',
                };
            });
            setOwnerList(ownersList);
        }
    }, [defaultValues]);

    const entityRegistry = useEntityRegistry();

    // Renders a search result in the select dropdown.
    const renderSearchResult = (entity: Entity) => {
        const displayName = entityRegistry.getDisplayName(entity.type, entity);
        return (
            <Select.Option value={entity.urn} key={entity.urn} tabIndex={0} aria-label={displayName}>
                <StyledOption>
                    {displayName}
                    <IconKeyboardEnter className="enter-icon" />
                </StyledOption>
            </Select.Option>
        );
    };

    const renderListItem = (entity: Entity) => {
        const avatarUrl =
            (entity.type === EntityType.CorpUser && (entity as CorpUser).editableProperties?.pictureLink) || undefined;
        const displayName = entityRegistry.getDisplayName(entity.type, entity);

        return (
            <List.Item.Meta
                avatar={
                    <CustomAvatar
                        name={displayName}
                        photoUrl={avatarUrl}
                        size={24}
                        isGroup={entity.type === EntityType.CorpGroup}
                    />
                }
                title={
                    <div className="f-text-medium-content f-text-single-line f-color-dark-black-s80">{displayName}</div>
                }
            />
        );
    };

    const { data: ownershipTypesData, loading: loadingTypes } = useListOwnershipTypesQuery({
        variables: {
            input: {},
        },
    });
    const ownershipTypes = useMemo(() => {
        const typesData = ownershipTypesData?.listOwnershipTypes?.ownershipTypes || [];
        return [...OWNERSHIP_DISPLAY_TYPES, ...typesData];
    }, [ownershipTypesData]);

    useEffect(() => {
        if (ownershipTypes.length) {
            setSelectedOwnerType({
                type: ownershipTypes[0].type,
                urn: ownershipTypes[0].urn,
                info: {
                    name: ownershipTypes[0].info?.name || ownershipTypes[0].urn,
                    description: ownershipTypes[0].info?.description || '',
                },
            });
        }
    }, [ownershipTypes]);

    // Invokes the search API as the owner types
    const handleSearch = (type: EntityType, text: string, searchQuery: any) => {
        searchQuery({
            variables: {
                input: {
                    type,
                    query: text,
                    start: 0,
                    count: 5,
                },
            },
        });
    };

    // Invokes the user search API for both users and groups.
    const handleActorSearch = (text: string) => {
        handleSearch(EntityType.CorpUser, text, userSearch);
    };

    const ownerResult = !inputValue || inputValue.length === 0 ? recommendedData : combinedSearchResults;

    const ownerSearchOptions = ownerResult?.map((result) => {
        return renderSearchResult(result);
    });

    const onModalClose = useCallback(() => {
        setInputValue('');
        setSelectedOwnerType(null);
        setOwnerList([]);
        onCloseModal();
    }, [onCloseModal]);

    const debouncedHandleSearch = debounce((text) => {
        handleActorSearch(text);
    }, 300);

    /**
     * When a owner search result is selected, add the new owner  to the selectedOwners
     * value: {ownerUrn: string, ownerEntityType: EntityType}
     */
    const onSelectOwner = (selectedValue: { key: string; label: React.ReactNode; value: string }) => {
        if (inputEl && inputEl.current) {
            (inputEl.current as any).blur();
        }

        // First check if we have a stored lastSelectedEntity that matches the URN
        let actor = lastSelectedEntity && lastSelectedEntity.urn === selectedValue.value ? lastSelectedEntity : null;

        // If we don't have a stored entity, try to find it in the search results or recommended data
        if (!actor) {
            /*
            Use both recommendedData and combinedSearchResults to find the selected entity
            This ensures we find the entity regardless of whether it's from search or recommendations
            */
            const allResults = [...(recommendedData || []), ...(combinedSearchResults || [])];
            const filteredActors = allResults
                .filter((entity) => entity.urn === selectedValue.value)
                .map((entity) => entity);

            if (filteredActors?.length) {
                [actor] = filteredActors;
            }
        }

        if (actor) {
            const tempOwnerList = [...ownerList];

            const existingOwnerIndex = tempOwnerList.findIndex((owner) => owner.urn === selectedValue.value);
            if (existingOwnerIndex !== -1) {
                tempOwnerList.splice(existingOwnerIndex, 1);
            }

            const newOwner: OwnerListType = {
                urn: selectedValue.value,
                entity: actor,
                ownershipTypeUrn: selectedOwnerType?.urn,
                ownershipTypeName: selectedOwnerType?.info?.name,
                ownershipType: selectedOwnerType?.type,
            };

            const newValues: OwnerListType[] = [...tempOwnerList, newOwner];
            setOwnerList(newValues);

            // Reset the lastSelectedEntity to avoid using stale data
            setLastSelectedEntity(null);
        }
    };

    const onDeSelectOwner = (urn: string) => {
        if (inputEl && inputEl.current) {
            (inputEl.current as any).blur();
        }

        const tempOwnerList = [...ownerList];

        const existingOwnerIndex = tempOwnerList.findIndex((owner) => owner.urn === urn);

        if (existingOwnerIndex !== -1) {
            tempOwnerList.splice(existingOwnerIndex, 1);
        }

        setOwnerList([...tempOwnerList]);
    };

    // When a owner type is selected, set the type as selected type.
    const onSelectOwnerType = (ownerShipType: SelectedOwnershipType) => {
        setSelectedOwnerType(ownerShipType);
    };

    // Only clear input value after a small delay to ensure selection is processed first
    function handleBlur() {
        setTimeout(() => {
            setInputValue('');
        }, 100);
    }

    /**
     * Generates a styled tag (chip) for the given owner type and title.
     *
     * @param ownerType - The type of the owner (e.g., BusinessOwner, DataSteward, TechnicalOwner).
     * @param chipTitle - The title to display inside the chip.
     *
     * @returns A styled Tag component representing the owner type.
     *
     * @remarks
     * - The chip's style and color are determined based on the owner type.
     * - Defaults to the DataSteward style if the owner type is not explicitly defined.
     */
    function getOwnerTypeChip(ownerType: string, chipTitle: string) {
        const tagProps = {
            [OwnershipType.BusinessOwner]: { color: 'success', className: 'f-tag-borderless label-tag ma-0' },
            [OwnershipType.DataSteward]: { className: 'label-tag grey-tag-light ma-0' },
            [OwnershipType.TechnicalOwner]: { className: 'label-tag base-tag ma-0' },
        };
        const { color, className } = tagProps[ownerType] || tagProps[OwnershipType.DataSteward];
        return (
            <Tag color={color} className={className}>
                {chipTitle}
            </Tag>
        );
    }

    /**
     * Handles the "OK" action for the modal.
     *
     * @remarks
     * - Compares the current owner list with the default values to determine owners to add and remove.
     * - Performs a batch update of owners by invoking the `batchUpdateOwners` function.
     * - Closes the modal if no changes are detected.
     */
    const onOk = useMemo(() => {
        return () => {
            const mapOwnerData = (item) => ({
                ownerUrn: item.urn,
                ownerEntityType: item.entity?.type || EntityType.CorpUser,
                type: item.ownershipType !== EntityType.CustomOwnershipType ? item.ownershipType : undefined,
                ownershipTypeUrn:
                    item.ownershipType === EntityType.CustomOwnershipType ? item.ownershipTypeUrn : undefined,
            });
            const ownersToAdd =
                ownerList
                    ?.filter(
                        (owner) =>
                            !defaultValues?.find(
                                (item) => item.urn === owner.urn && item.ownershipType?.urn === owner.ownershipTypeUrn,
                            ),
                    )
                    .map(mapOwnerData) ?? ownerList.map(mapOwnerData);

            const ownersToRemove =
                defaultValues
                    ?.filter((item) => !ownerList.find((owner) => item.urn === owner.urn))
                    .map((item) => {
                        return {
                            ownerUrn: item.urn,
                            ownerEntityType: item.entity?.type || EntityType.CorpUser,
                            type: item.ownerType,
                            ownershipTypeUrn:
                                item.ownerType === OwnershipType.Custom ? item.ownershipType?.urn : undefined,
                        };
                    }) ?? [];

            if (ownersToAdd.length !== 0 || ownersToRemove.length !== 0) {
                batchUpdateOwners(ownersToRemove, ownersToAdd);
            } else {
                onModalClose();
            }
        };
    }, [ownerList, defaultValues, batchUpdateOwners, onModalClose]);

    useEffect(() => {
        // This effect runs when isVisible changes (when modal opens/closes)
        if (!isVisible) {
            // Clean up state when modal closes
            setInputValue('');
            setSelectedOwnerType(null);
            setOwnerList([]);
            setSelectValue(null);
            setLastSelectedEntity(null);
        }
    }, [isVisible]);

    /**
     * Handles the selection of an owner from the dropdown
     * Updates the selected value and stores the full entity details
     * @param asset - The selected asset from the dropdown
     */
    const onOwnerSelect = (asset) => {
        setSelectValue(asset);
        const allResults = [...(recommendedData || []), ...(combinedSearchResults || [])];
        const selectedEntity = allResults.find((entity) => entity.urn === asset.value);
        if (selectedEntity) {
            setLastSelectedEntity(selectedEntity);
        }
    };

    /**
     * Adds the selected owner to the list of owners
     * Calls the parent component's onSelectOwner callback and resets the selection
     */
    const onAdd = () => {
        if (selectValue) {
            onSelectOwner(selectValue);
            setSelectValue(null);
        }
    };

    return (
        <Modal
            centered
            className="f-modal"
            visible={isVisible}
            onCancel={onModalClose}
            title={<h1 className="f-modal-title">Update Owners</h1>}
            footer={
                <>
                    <Button type="default" onClick={onModalClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => onOk()}
                        disabled={loadingMutation || loadingSearch || loadingTypes}
                    >
                        Update
                    </Button>
                </>
            }
        >
            <div className="f-modal-text">
                Ensure that your data product includes designated owners, specifying the required owner types to
                establish clear accountability and governance.
            </div>
            <Row wrap={false} gutter={4} align="middle" className="mx-0 mb-3">
                <Col flex="auto">
                    <SelectInput
                        autoFocus
                        showArrow
                        autoClearSearchValue
                        showSearch
                        searchValue={inputValue}
                        value={selectValue}
                        labelInValue
                        size="middle"
                        ref={inputEl}
                        filterOption={false}
                        aria-label="Select Owner"
                        defaultActiveFirstOption={false}
                        placeholder={
                            <>
                                <SearchOutlined className="f-color-dark-black-s80" /> Search for users...
                            </>
                        }
                        getPopupContainer={(triggerNode) => triggerNode.parentNode} // Control dropdown container
                        onSelect={onOwnerSelect}
                        onSearch={(value: string) => {
                            // eslint-disable-next-line react/prop-types
                            debouncedHandleSearch(value);
                            // eslint-disable-next-line react/prop-types
                            setInputValue(value);
                        }}
                        onBlur={handleBlur}
                    >
                        {ownerSearchOptions}
                    </SelectInput>
                </Col>
                <Col flex="none">
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                        overlay={
                            <Menu className="f-dropdown-small">
                                {ownershipTypes.map((ownershipType) => {
                                    const ownershipTypeName = ownershipType?.info?.name || ownershipType?.urn || '';
                                    return (
                                        <Menu.Item
                                            key={`${ownershipType.urn}`}
                                            onClick={() => {
                                                onSelectOwnerType({
                                                    type: ownershipType.type,
                                                    urn: ownershipType.urn,
                                                    info: {
                                                        name: ownershipType.info?.name || ownershipType.urn,
                                                        description: ownershipType.info?.description || '',
                                                    },
                                                });
                                            }}
                                        >
                                            {ownershipTypeName}
                                        </Menu.Item>
                                    );
                                })}
                            </Menu>
                        }
                    >
                        <StyledButton type="default">
                            <span>{selectedOwnerType?.info?.name || selectedOwnerType?.urn}</span>
                            <IconArrowDown />
                        </StyledButton>
                    </Dropdown>
                </Col>
                <Col flex="none">
                    <StyledIconButton onClick={onAdd} icon={<PlusOutlined />} className="f-btn-pri-outline" />
                </Col>
            </Row>
            {ownerList.map((item) => {
                return (
                    <List className="f-list-medium">
                        <List.Item>
                            {item.entity && renderListItem(item.entity)}
                            {getOwnerTypeChip(item.ownershipType || '', item.ownershipTypeName || '')}
                            <Dropdown
                                trigger={['click']}
                                placement="bottomRight"
                                overlay={
                                    <Menu className="f-dropdown-small" onClick={() => onDeSelectOwner(item.urn)}>
                                        <Menu.Item>Remove</Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button
                                    type="text"
                                    title="More Actions"
                                    icon={<IconMoreVert />}
                                    aria-label="More Actions"
                                    className="icon-button ma-0"
                                />
                            </Dropdown>
                        </List.Item>
                    </List>
                );
            })}
        </Modal>
    );
};

export default UpdateOwnersModal;
